import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
} from '@angular/core';
import { filterNil } from '@ngneat/elf';
import pdfjsLib from 'src/assets/pdfjs-config';

import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  forkJoin,
  from,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';
import { FLAGS, FlagsService } from 'src/app/services/flags.service';

const pageOperator$ = (
  pdfProxy: pdfjsLib.PDFDocumentProxy,
  pageNumber: number,
  scaleFactor: number,
  maxWidth: number,
  maxHeight?: number,
) =>
  from(pdfProxy.getPage(pageNumber)).pipe(
    map((page) => {
      const originalViewport = page.getViewport({ scale: 1 });
      const width = Math.floor(
        maxHeight
          ? Math.min(maxWidth, (maxHeight / originalViewport.height) * originalViewport.width)
          : maxWidth,
      );
      const viewport = page.getViewport({
        scale: (width / originalViewport.width) * scaleFactor,
      });
      const height = Math.floor((width / originalViewport.width) * originalViewport.height);

      return {
        page,
        viewport,
        height,
        width,
      };
    }),
  );

interface PDFPageViewModel {
  page: pdfjsLib.PDFPageProxy;
  viewport: pdfjsLib.PageViewport;
  height: number;
  width: number;
}

interface PDFContainerSize {
  height: number;
  width: number;
}

@Component({
  selector: 'app-pdf-fragment-container',
  templateUrl: './pdf-fragment-container.component.html',
  styleUrls: ['./pdf-fragment-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PdfFragmentContainerComponent implements AfterViewInit {
  @Input() set src(value: string | undefined) {
    this._srcSubject.next(value);
  }
  @Input() page?: number;

  private _srcSubject = new BehaviorSubject<string | undefined>(undefined);
  private hostSize$ = new Observable<PDFContainerSize>((subscriber) => {
    const resizeObserver = new ResizeObserver((entries) => {
      const contentRect = entries[0]?.contentRect;
      subscriber.next(this.getContainerSize(contentRect?.width, contentRect?.height));
    });
    resizeObserver.observe(this.elementRef.nativeElement);

    return () => resizeObserver.disconnect();
  }).pipe(
    filter(({ width }) => width > 0),
    distinctUntilChanged(
      (previous, current) => previous.width === current.width && previous.height === current.height,
    ),
  );
  scaleFactor: number;

  constructor(
    private flagsService: FlagsService,
    private elementRef: ElementRef,
    private cdRef: ChangeDetectorRef,
  ) {
    this.scaleFactor = this.flagsService.isFlagEnabled(FLAGS.ENABLE_PDF_UPSCALE)
      ? (this.flagsService.featureFlagsVariables.enable_pdf_upscale.pdf_scale_factor as number)
      : 1.0;
  }

  data$: Observable<PDFPageViewModel[]> = of<PDFPageViewModel[]>([]);

  ngAfterViewInit(): void {
    const pdf$ = this._srcSubject.pipe(
      filterNil(),
      distinctUntilChanged(),
      switchMap((src) => from(pdfjsLib.getDocument(src).promise)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.data$ = combineLatest([pdf$, this.hostSize$]).pipe(
      switchMap(([pdf, size]) => this.pages(pdf, size)),
    );

    this.cdRef.detectChanges();
  }

  trackByPageNumber(_: number, item: PDFPageViewModel): number {
    return item.page.pageNumber;
  }

  private pages(
    pdf: pdfjsLib.PDFDocumentProxy,
    { height, width }: PDFContainerSize,
  ): Observable<PDFPageViewModel[]> {
    return this.page
      ? pageOperator$(pdf, this.page, this.scaleFactor, width, height).pipe(map((page) => [page]))
      : forkJoin(
          Array.from({
            length: pdf.numPages,
          }).map((_, index) => pageOperator$(pdf, index + 1, this.scaleFactor, width)),
        );
  }

  private getContainerSize(width?: number, height?: number): PDFContainerSize {
    const host = this.elementRef.nativeElement as HTMLElement;
    return {
      height: this.page ? Math.floor(height ?? host.offsetHeight) : 0,
      width: Math.floor(width ?? host.offsetWidth),
    };
  }
}
